<template>
  <div class="map-editor" @click="onEditorAreaClick">
    <!-- No plan selected -->
    <div v-if="!ws.currentPlan.value" class="no-plan-hint">
      Select or add a battleplan to begin editing.
    </div>

    <template v-else>
      <!-- Tool picker row + attached settings sub-bar -->
      <div class="toolbar-stack">
        <div class="toolbar">
          <StSegmentedControl v-model="toolModel" :options="toolOptions" size="sm" />

          <div class="st-toolbar-spacer" />

          <StDropdown align="right">
            <template #trigger>
              <StButton variant="ghost" size="sm" icon="wand-sparkles">
                Actions
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="actions-caret" />
              </StButton>
            </template>
            <StMenuItem icon="arrows-left-right" @click="openMirrorModal">
              Mirror Attacker ↔ Defender
            </StMenuItem>
          </StDropdown>

          <StButton
            variant="secondary"
            size="sm"
            icon="rotate-left"
            :disabled="!ws.canUndo.value"
            @click="doUndo"
          >
            Undo
          </StButton>
        </div>

        <ToolSubbar
          v-model:obj-type="objTypeModel"
          v-model:terrain-type="terrainTypeModel"
          v-model:terrain-size="terrainSizeModel"
          v-model:zone-type="zoneTypeModel"
          v-model:mirror="mirrorModel"
          v-model:icon-name="iconNameModel"
          :context="subbarContext"
          :selected="!!selectedKind"
          :arrow-first-click="arrowFirstClick"
        />

        <PropsBar
          :kind="selectedKind"
          :element="selectedElement"
          @field="updateSelectedField"
          @delete="deleteSelected"
        />
      </div>

      <!-- SVG board -->
      <div class="board-container">
        <svg
          ref="svgEl"
          :viewBox="`0 0 ${W} ${H}`"
          class="editor-svg"
          :style="{ cursor: activeTool === 'Select' ? 'default' : 'crosshair' }"
          xmlns="http://www.w3.org/2000/svg"
          @click.self="onSvgClick"
          @click="onSvgClickEvent"
          @mousemove="onSvgMouseMove"
          @mousedown="onSvgMouseDown"
        >
          <MapBoardGrid />

          <!-- ====== RENDERED ELEMENTS ====== -->

          <!-- Zones. Only interactive when the Zone tool is active, so other
               tools can place elements directly on top of (full-coverage) zones. -->
          <g
            v-for="(zone, i) in editMap.zones"
            :key="'z-' + i"
            class="element-group"
            :class="{ selected: selectedKind === 'Zone' && selectedIdx === i }"
            :style="{
              pointerEvents: activeTool === 'Zone' || activeTool === 'Select' ? 'all' : 'none',
            }"
            @click.stop="selectElement('Zone', i)"
            @mousedown.stop="startDragZone(i, $event)"
          >
            <rect
              :x="s(Math.min(zone.x1, zone.x2))"
              :y="s(Math.min(zone.y1, zone.y2))"
              :width="s(Math.abs(zone.x2 - zone.x1))"
              :height="s(Math.abs(zone.y2 - zone.y1))"
              :class="'zone zone-' + zone.type"
              style="cursor: move"
            />
            <!-- Zone label (draggable to set an optional offset when selected) -->
            <text
              v-if="!zone.hideLabel"
              :x="s((zone.x1 + zone.x2) / 2 + (zone.labelDx ?? 0))"
              :y="s((zone.y1 + zone.y2) / 2 + (zone.labelDy ?? 0))"
              class="zone-label"
              :class="{
                'zone-label-draggable': selectedKind === 'Zone' && selectedIdx === i,
                'zone-label-dragging': draggingLabelIdx === i,
              }"
              @mousedown="onZoneLabelMouseDown(i, $event)"
            >
              <tspan
                v-for="(line, li) in zoneDisplayLabelLines(zone)"
                :key="li"
                :x="s((zone.x1 + zone.x2) / 2 + (zone.labelDx ?? 0))"
                :dy="zoneLabelLineDy(zoneDisplayLabelLines(zone).length, li)"
              >
                {{ line }}
              </tspan>
            </text>
            <!-- Resize handles (corners + edges), shown when selected -->
            <rect
              v-for="h in (activeTool === 'Zone' || activeTool === 'Select') &&
              selectedKind === 'Zone' &&
              selectedIdx === i
                ? zoneHandles(zone)
                : []"
              :key="h.key"
              :x="s(h.x) - HANDLE"
              :y="s(h.y) - HANDLE"
              :width="HANDLE * 2"
              :height="HANDLE * 2"
              class="zone-resize-handle"
              :style="{ cursor: h.cursor }"
              @mousedown.stop="startResizeZone(i, h.key, $event)"
              @click.stop
            />
          </g>

          <!-- Zone drag preview -->
          <rect
            v-if="creatingZone && zonePreview"
            :x="s(Math.min(zonePreview.x1, zonePreview.x2))"
            :y="s(Math.min(zonePreview.y1, zonePreview.y2))"
            :width="s(Math.abs(zonePreview.x2 - zonePreview.x1))"
            :height="s(Math.abs(zonePreview.y2 - zonePreview.y1))"
            :class="'zone zone-' + zoneType"
            style="pointer-events: none; opacity: 0.5"
          />
          <!-- Zone first-click anchor -->
          <circle
            v-if="creatingZone && zoneAnchor"
            :cx="s(zoneAnchor!.x)"
            :cy="s(zoneAnchor!.y)"
            r="4"
            class="anchor-dot"
            style="pointer-events: none"
          />

          <!-- Arrows -->
          <MapArrowMarker />
          <g
            v-for="(arrow, i) in editMap.arrows ?? []"
            :key="'a-' + i"
            class="element-group"
            :class="{ selected: selectedKind === 'Arrow' && selectedIdx === i }"
            @click.stop="selectElement('Arrow', i)"
          >
            <line
              :x1="s(arrow.x1)"
              :y1="s(arrow.y1)"
              :x2="s(arrow.x2)"
              :y2="s(arrow.y2)"
              class="map-arrow"
              marker-end="url(#editor-arrowhead)"
            />
            <text
              v-if="arrow.text"
              :x="s((arrow.x1 + arrow.x2) / 2)"
              :y="s((arrow.y1 + arrow.y2) / 2) - 6"
              class="arrow-label"
            >
              {{ arrow.text }}
            </text>
            <!-- Wider invisible hit area -->
            <line
              :x1="s(arrow.x1)"
              :y1="s(arrow.y1)"
              :x2="s(arrow.x2)"
              :y2="s(arrow.y2)"
              stroke="transparent"
              stroke-width="12"
            />
            <!-- Selection handles at endpoints -->
            <template v-if="selectedKind === 'Arrow' && selectedIdx === i">
              <circle
                :cx="s(arrow.x1)"
                :cy="s(arrow.y1)"
                r="5"
                class="selection-handle"
                @mousedown.stop="startDragArrowEndpoint(i, 'start', $event)"
              />
              <circle
                :cx="s(arrow.x2)"
                :cy="s(arrow.y2)"
                r="5"
                class="selection-handle"
                @mousedown.stop="startDragArrowEndpoint(i, 'end', $event)"
              />
            </template>
          </g>

          <!-- Arrow first-click anchor + preview line -->
          <template v-if="activeTool === 'Arrow' && arrowFirstClick">
            <circle
              :cx="s(arrowStart!.x)"
              :cy="s(arrowStart!.y)"
              r="4"
              class="anchor-dot"
              style="pointer-events: none"
            />
            <line
              v-if="mouseBoard"
              :x1="s(arrowStart!.x)"
              :y1="s(arrowStart!.y)"
              :x2="s(mouseBoard.x)"
              :y2="s(mouseBoard.y)"
              class="map-arrow preview-line"
              style="pointer-events: none; opacity: 0.5"
              marker-end="url(#editor-arrowhead)"
            />
          </template>

          <!-- Mirror ghosts — non-interactive derived copies of mirrored objectives/terrain -->
          <!-- Rendered before interactive elements so handles remain clickable -->
          <g v-for="(ghost, i) in ghostTerrain" :key="'gt-' + i" style="pointer-events: none">
            <circle
              :cx="s(ghost.x)"
              :cy="s(ghost.y)"
              :r="terrainRadius(ghost.size)"
              :class="'terrain terrain-' + ghost.type"
              class="ghost-element"
            />
            <path
              :d="terrainIconPath(ghost.type)"
              :transform="terrainIconTransform(ghost.type, s(ghost.x), s(ghost.y), 14)"
              class="terrain-icon-svg ghost-element"
            />
          </g>
          <g v-for="(ghost, i) in ghostObjectives" :key="'go-' + i" style="pointer-events: none">
            <circle
              :cx="s(ghost.x)"
              :cy="s(ghost.y)"
              :r="s(OBJ_RADIUS)"
              :class="'objective objective-' + ghost.type"
              class="ghost-element"
            />
            <path
              v-if="objectiveIconDef(ghost.type)"
              :d="objectiveIconPath(ghost.type)"
              :transform="objectiveIconTransform(ghost.type, s(ghost.x), s(ghost.y), 22)"
              class="objective-icon-svg ghost-label"
            />
            <text v-else :x="s(ghost.x)" :y="s(ghost.y)" class="objective-label ghost-label">
              {{ objectiveDisplayLabel(ghost.type) }}
            </text>
          </g>

          <!-- Terrain -->
          <g
            v-for="(t, i) in editMap.terrain"
            :key="'t-' + i"
            class="element-group"
            :class="{ selected: selectedKind === 'Terrain' && selectedIdx === i }"
            style="cursor: grab"
            @click.stop="selectElement('Terrain', i)"
            @mousedown.stop="startDragPoint('Terrain', i, $event)"
          >
            <circle
              :cx="s(t.x)"
              :cy="s(t.y)"
              :r="terrainRadius(t.size)"
              :class="'terrain terrain-' + t.type"
            />
            <path
              :d="terrainIconPath(t.type)"
              :transform="terrainIconTransform(t.type, s(t.x), s(t.y), 14)"
              class="terrain-icon-svg"
              style="pointer-events: none"
            />
            <circle
              v-if="selectedKind === 'Terrain' && selectedIdx === i"
              :cx="s(t.x)"
              :cy="s(t.y)"
              r="4"
              class="selection-handle"
            />
          </g>

          <!-- Objectives -->
          <g
            v-for="(obj, i) in editMap.objectives"
            :key="'o-' + i"
            class="element-group"
            :class="{ selected: selectedKind === 'Objective' && selectedIdx === i }"
            style="cursor: grab"
            @click.stop="selectElement('Objective', i)"
            @mousedown.stop="startDragPoint('Objective', i, $event)"
          >
            <circle
              :cx="s(obj.x)"
              :cy="s(obj.y)"
              :r="s(OBJ_RADIUS)"
              :class="'objective objective-' + obj.type"
            />
            <path
              v-if="objectiveIconDef(obj.type)"
              :d="objectiveIconPath(obj.type)"
              :transform="objectiveIconTransform(obj.type, s(obj.x), s(obj.y), 22)"
              class="objective-icon-svg"
              style="pointer-events: none"
            />
            <text v-else :x="s(obj.x)" :y="s(obj.y)" class="objective-label">
              {{ objectiveDisplayLabel(obj.type) }}
            </text>
            <circle
              v-if="selectedKind === 'Objective' && selectedIdx === i"
              :cx="s(obj.x)"
              :cy="s(obj.y)"
              r="4"
              class="selection-handle"
            />
          </g>

          <!-- Labels -->
          <g
            v-for="(label, i) in editMap.labels ?? []"
            :key="'l-' + i"
            class="element-group"
            :class="{ selected: selectedKind === 'Label' && selectedIdx === i }"
            style="cursor: grab"
            @click.stop="selectElement('Label', i)"
            @mousedown.stop="startDragPoint('Label', i, $event)"
          >
            <text
              :x="s(label.x)"
              :y="s(label.y)"
              class="map-label"
              :style="label.fontSize ? `font-size: ${label.fontSize}px` : ''"
              :transform="
                label.rotation ? `rotate(${label.rotation} ${s(label.x)} ${s(label.y)})` : undefined
              "
            >
              {{ label.text }}
            </text>
            <circle
              v-if="selectedKind === 'Label' && selectedIdx === i"
              :cx="s(label.x)"
              :cy="s(label.y)"
              r="4"
              class="selection-handle"
            />
          </g>

          <!-- Icons -->
          <g
            v-for="(icon, i) in editMap.icons ?? []"
            :key="'ic-' + i"
            class="element-group"
            :class="{ selected: selectedKind === 'Icon' && selectedIdx === i }"
            style="cursor: grab"
            @click.stop="selectElement('Icon', i)"
            @mousedown.stop="startDragPoint('Icon', i, $event)"
          >
            <!-- Transparent hit area matching the glyph footprint, so the whole
                 icon is clickable/draggable rather than just the painted strokes. -->
            <circle
              :cx="s(icon.x)"
              :cy="s(icon.y)"
              :r="(icon.size ?? DEFAULT_ICON_SIZE) / 2"
              class="icon-hit"
            />
            <path
              :d="mapIconPath(icon.icon)"
              :transform="
                mapIconTransform(icon.icon, s(icon.x), s(icon.y), icon.size ?? DEFAULT_ICON_SIZE)
              "
              class="map-icon-svg"
              style="pointer-events: none"
            />
            <circle
              v-if="selectedKind === 'Icon' && selectedIdx === i"
              :cx="s(icon.x)"
              :cy="s(icon.y)"
              r="4"
              class="selection-handle"
            />
          </g>

          <!-- Cursor crosshair during placement -->
          <g
            v-if="mouseBoard && !isDragging && activeTool !== 'Select'"
            class="crosshair"
            style="pointer-events: none"
          >
            <line
              :x1="s(mouseBoard.x)"
              y1="0"
              :x2="s(mouseBoard.x)"
              :y2="H"
              class="crosshair-line"
            />
            <line
              x1="0"
              :y1="s(mouseBoard.y)"
              :x2="W"
              :y2="s(mouseBoard.y)"
              class="crosshair-line"
            />
            <text :x="s(mouseBoard.x) + 6" :y="s(mouseBoard.y) - 4" class="coord-label">
              {{ mouseBoard.x }}", {{ mouseBoard.y }}"
            </text>
          </g>
        </svg>
      </div>

      <MapStatusBar :map="editMap" :mouse-board="mouseBoard" />

      <MirrorZonesModal
        v-if="mirrorModalOpen"
        :attacker-count="zoneCounts.attacker"
        :defender-count="zoneCounts.defender"
        @apply="onApplyMirror"
        @cancel="mirrorModalOpen = false"
      />

      <NameModal
        :open="labelModalOpen"
        :title="labelModalConfig.title"
        :field-label="labelModalConfig.fieldLabel"
        :placeholder="labelModalConfig.placeholder"
        :confirm-label="labelModalConfig.confirmLabel"
        :allow-empty="labelModalConfig.allowEmpty"
        @confirm="confirmLabelModal"
        @close="closeLabelModal"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import {
  type IMapObjective,
  type IMapTerrain,
  type IMapZone,
  type IMapLabel,
  type IMapArrow,
  type IMapIcon,
  ObjectiveType,
  TerrainType,
  TerrainSize,
  ZoneType,
  Mirror,
} from '@scrolldex/shared';
import { useWorkspace } from '../state/workspace.js';
import { useSettings } from '../state/settings.js';
import { snapToGrid, svgToBoard } from './mapEditorMath.js';
import {
  SCALE,
  W,
  H,
  OBJ_RADIUS,
  s,
  terrainRadius,
  terrainIconPath,
  terrainIconTransform,
  objectiveIconDef,
  objectiveIconPath,
  objectiveIconTransform,
  mapIconPath,
  mapIconTransform,
  DEFAULT_ICON_SIZE,
} from './mapRender.js';
import { useMapMirror } from '../composables/useMapMirror.js';
import { useMapSelection } from '../composables/useMapSelection.js';
import { useMapWorkingCopy } from '../composables/useMapWorkingCopy.js';
import {
  type ToolKind,
  TOOLS,
  objectiveDisplayLabel,
  zoneDisplayLabelLines,
  zoneLabelLineDy,
} from './editor/labels.js';
import {
  StSegmentedControl,
  StDropdown,
  StMenuItem,
  StButton,
  type SegmentOption,
} from './ui/index.js';
import MirrorZonesModal from './editor/MirrorZonesModal.vue';
import NameModal from './NameModal.vue';
import ToolSubbar from './editor/ToolSubbar.vue';
import PropsBar from './editor/PropsBar.vue';
import MapBoardGrid from './editor/MapBoardGrid.vue';
import MapArrowMarker from './editor/MapArrowMarker.vue';
import MapStatusBar from './editor/MapStatusBar.vue';

const ws = useWorkspace();
const settings = useSettings();

// Board geometry constants (SCALE/W/H/OBJ_RADIUS/s) and terrain-icon math live
// in mapRender.ts so the component stays focused on interaction state.

// ─── Toolbar state ────────────────────────────────────────────────────────────
const activeTool = ref<ToolKind>('Select');

// Tool picker as a segmented control. Setting it routes through selectTool so
// switching tools clears the current selection (see selectTool below).
const toolOptions: SegmentOption[] = TOOLS.map((t) => ({
  value: t.key,
  label: t.label,
  icon: t.icon,
}));
const toolModel = computed<ToolKind>({
  get: () => activeTool.value,
  set: (v) => selectTool(v as ToolKind),
});

// Placement defaults (also the sticky targets the sub-bar models write to).
const objType = ref<ObjectiveType>(ObjectiveType.Regular);
const terrainType = ref<TerrainType>(TerrainType.Ruin);
const terrainSize = ref<TerrainSize>(TerrainSize.Medium);
const zoneType = ref<ZoneType>(ZoneType.Attacker);
// Placement default for the Icon tool (freeform FA name; sub-bar picker writes it).
const iconName = ref<string>('circle');
// Mirror defaults are per-tool by default (like type/size). The "tools share
// mirror" setting routes both tools through `sharedMirror` instead.
const objMirror = ref<Mirror | 'none'>('none');
const terrainMirror = ref<Mirror | 'none'>('none');
const sharedMirror = ref<Mirror | 'none'>('none');

// Resolve / set the placement mirror default for a tool, honoring the
// "tools share mirror" setting.
function placementMirror(context: 'Objective' | 'Terrain'): Mirror | 'none' {
  if (settings.toolsShareMirror) return sharedMirror.value;
  return context === 'Terrain' ? terrainMirror.value : objMirror.value;
}
function setPlacementMirror(context: 'Objective' | 'Terrain', v: Mirror | 'none') {
  if (settings.toolsShareMirror) sharedMirror.value = v;
  else if (context === 'Terrain') terrainMirror.value = v;
  else objMirror.value = v;
}

// Select an element just placed — unless the user turned off auto-select.
function maybeSelectPlaced(kind: ToolKind, idx: number, suppress = false) {
  if (!settings.autoSelectPlaced) return;
  selectElement(kind, idx);
  // Swallow the trailing click after a drag-created element so it doesn't
  // immediately deselect what we just selected.
  if (suppress) suppressNextClick = true;
}

// ─── Working copy of the map ──────────────────────────────────────────────────
// Edits happen against a local deep copy; commit writes the plain map back to
// workspace state after each mutation.
const { editMap, syncEditMapFromStore, commit } = useMapWorkingCopy(
  ws.currentMap,
  ws.updateCurrentMap,
);

// ─── Selection ────────────────────────────────────────────────────────────────
const { selectedKind, selectedIdx, selectedElement, setSelectedElement, deselectElement } =
  useMapSelection(editMap);

function selectElement(kind: ToolKind, idx: number) {
  setSelectedElement(kind, idx);
  // If a drag ended directly on an element, its @click.stop handler runs here
  // instead of the click handlers that would normally clear this flag — clear it
  // so a pending suppress doesn't swallow a later, unrelated click.
  suppressNextClick = false;
}

// When the store's current plan changes, reload the working copy
watch(
  () => ws.currentPlan.value,
  () => {
    syncEditMapFromStore();
    resetPlacementState();
    deselectElement();
  },
);

// ─── Actions menu: mirror Attacker ↔ Defender ─────────────────────────────────
// Ghosts, zone counts, and the mirror operation live in the useMapMirror
// composable; the component only owns the modal's open state.
const mirrorModalOpen = ref(false);
const { ghostObjectives, ghostTerrain, zoneCounts, applyZoneMirror } = useMapMirror(editMap, {
  commit,
  deselect: deselectElement,
});

function openMirrorModal() {
  mirrorModalOpen.value = true;
}

function onApplyMirror(payload: { mode: Mirror; from: ZoneType }) {
  applyZoneMirror(payload);
  mirrorModalOpen.value = false;
}

// Switching tools clears the current selection so the sub-bar returns to setting
// placement defaults rather than editing the (now irrelevant) selected element.
function selectTool(kind: ToolKind) {
  activeTool.value = kind;
  deselectElement();
  resetPlacementState();
}

// Numeric coordinate fields — snap to 0.5" grid and clamp to board
const COORD_FIELDS_X = new Set(['x', 'x1', 'x2']);
const COORD_FIELDS_Y = new Set(['y', 'y1', 'y2']);

function updateSelectedField(field: string, value: unknown) {
  if (selectedKind.value === null || selectedIdx.value === null || !selectedElement.value) return;
  const idx = selectedIdx.value;

  // Coerce value
  let coerced: unknown = value;
  if (field === 'mirror') {
    // 'none' → omit the field entirely (undefined)
    coerced = value === 'none' || value === '' ? undefined : value;
  } else if (field === 'hideLabel') {
    coerced = Boolean(value);
  } else if (field === 'fontSize') {
    // Allow empty → undefined (removes the field)
    const s = String(value).trim();
    coerced = s === '' ? undefined : Number(s);
  } else if (field === 'rotation') {
    // Normalize to a 0–270 multiple of 90; 0 → undefined (omit the field).
    const n = ((Number(value) % 360) + 360) % 360;
    coerced = n === 0 ? undefined : n;
  } else if (field === 'size') {
    // Optional icon glyph size; empty → undefined (back to the renderer default).
    const sv = String(value).trim();
    coerced = sv === '' ? undefined : Number(sv);
  } else if (field === 'legend') {
    // Optional icon legend label; all-blank → undefined (no map-key entry).
    const sv = String(value);
    coerced = sv.trim() === '' ? undefined : sv;
  } else if (field === 'labelDx' || field === 'labelDy') {
    // Optional zone label offset; empty → undefined (back to centered).
    const s = String(value).trim();
    coerced = s === '' ? undefined : snapToGrid(Number(s));
  } else if (field === 'label') {
    // Optional custom zone label: all-blank → undefined (falls back to type
    // default). Keep the raw value otherwise so spaces survive live typing.
    const s = String(value);
    coerced = s.trim() === '' ? undefined : s;
  } else if (field === 'text') {
    // Optional text on arrow: empty string → undefined
    const s = String(value);
    if (selectedKind.value === 'Arrow') {
      coerced = s === '' ? undefined : s;
    } else {
      coerced = s;
    }
  } else if (COORD_FIELDS_X.has(field) || COORD_FIELDS_Y.has(field)) {
    let n = snapToGrid(Number(value));
    if (COORD_FIELDS_X.has(field)) n = Math.max(0, Math.min(60, n));
    if (COORD_FIELDS_Y.has(field)) n = Math.max(0, Math.min(44, n));
    coerced = n;
  } else if (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '') {
    coerced = Number(value);
  }

  // Build updated element, omitting undefined fields (mirrors the 'none' → absent contract)
  const base = { ...selectedElement.value };
  if (coerced === undefined) {
    delete (base as Record<string, unknown>)[field];
  } else {
    (base as Record<string, unknown>)[field] = coerced;
  }

  switch (selectedKind.value) {
    case 'Objective':
      editMap.value = {
        ...editMap.value,
        objectives: editMap.value.objectives.map((o, i) =>
          i === idx ? (base as IMapObjective) : o,
        ),
      };
      break;
    case 'Terrain':
      editMap.value = {
        ...editMap.value,
        terrain: editMap.value.terrain.map((t, i) => (i === idx ? (base as IMapTerrain) : t)),
      };
      break;
    case 'Zone':
      editMap.value = {
        ...editMap.value,
        zones: editMap.value.zones.map((z, i) => (i === idx ? (base as IMapZone) : z)),
      };
      break;
    case 'Label':
      editMap.value = {
        ...editMap.value,
        labels: (editMap.value.labels ?? []).map((l, i) => (i === idx ? (base as IMapLabel) : l)),
      };
      break;
    case 'Arrow':
      editMap.value = {
        ...editMap.value,
        arrows: (editMap.value.arrows ?? []).map((a, i) => (i === idx ? (base as IMapArrow) : a)),
      };
      break;
    case 'Icon':
      editMap.value = {
        ...editMap.value,
        icons: (editMap.value.icons ?? []).map((ic, i) => (i === idx ? (base as IMapIcon) : ic)),
      };
      break;
  }
  commit();
}

// ─── Contextual sub-bar models ────────────────────────────────────────────────
// The sub-bar pickers drive the SELECTED element when one is selected, otherwise
// the placement defaults. Setting either one is "sticky": it updates the default
// (so the next placed element inherits it) AND the selected element if present.
// `subbarContext` decides which picker group the sub-bar shows: the selection's
// kind when something is selected, otherwise the active tool.
const subbarContext = computed<ToolKind>(() => selectedKind.value ?? activeTool.value);

const selectedObjective = computed(() =>
  selectedKind.value === 'Objective' ? (selectedElement.value as IMapObjective) : null,
);
const selectedTerrain = computed(() =>
  selectedKind.value === 'Terrain' ? (selectedElement.value as IMapTerrain) : null,
);
const selectedZone = computed(() =>
  selectedKind.value === 'Zone' ? (selectedElement.value as IMapZone) : null,
);
const selectedIcon = computed(() =>
  selectedKind.value === 'Icon' ? (selectedElement.value as IMapIcon) : null,
);

const objTypeModel = computed<ObjectiveType>({
  get: () => selectedObjective.value?.type ?? objType.value,
  set: (v) => {
    objType.value = v;
    if (selectedObjective.value) updateSelectedField('type', v);
  },
});

const terrainTypeModel = computed<TerrainType>({
  get: () => selectedTerrain.value?.type ?? terrainType.value,
  set: (v) => {
    terrainType.value = v;
    if (selectedTerrain.value) updateSelectedField('type', v);
  },
});

const terrainSizeModel = computed<TerrainSize>({
  get: () => selectedTerrain.value?.size ?? terrainSize.value,
  set: (v) => {
    terrainSize.value = v;
    if (selectedTerrain.value) updateSelectedField('size', v);
  },
});

const zoneTypeModel = computed<ZoneType>({
  get: () => selectedZone.value?.type ?? zoneType.value,
  set: (v) => {
    zoneType.value = v;
    if (selectedZone.value) updateSelectedField('type', v);
  },
});

const iconNameModel = computed<string>({
  get: () => selectedIcon.value?.icon ?? iconName.value,
  set: (v) => {
    iconName.value = v;
    if (selectedIcon.value) updateSelectedField('icon', v);
  },
});

const mirrorModel = computed<Mirror | 'none'>({
  get: () => {
    if (selectedTerrain.value) return selectedTerrain.value.mirror ?? 'none';
    if (selectedObjective.value) return selectedObjective.value.mirror ?? 'none';
    // Placement: the mirror picker only shows for Objective/Terrain contexts.
    return placementMirror(subbarContext.value === 'Terrain' ? 'Terrain' : 'Objective');
  },
  set: (v) => {
    if (selectedTerrain.value) {
      setPlacementMirror('Terrain', v);
      updateSelectedField('mirror', v);
    } else if (selectedObjective.value) {
      setPlacementMirror('Objective', v);
      updateSelectedField('mirror', v);
    } else if (subbarContext.value === 'Terrain') {
      setPlacementMirror('Terrain', v);
    } else {
      setPlacementMirror('Objective', v);
    }
  },
});

// ─── SVG coordinate conversion ────────────────────────────────────────────────
const svgEl = ref<SVGSVGElement | null>(null);

function svgPoint(clientX: number, clientY: number): { x: number; y: number } | null {
  if (!svgEl.value) return null;
  const rect = svgEl.value.getBoundingClientRect();
  // Scale from rendered rect to viewBox units
  const scaleX = W / rect.width;
  const scaleY = H / rect.height;
  const svgX = (clientX - rect.left) * scaleX;
  const svgY = (clientY - rect.top) * scaleY;
  const bx = snapToGrid(svgToBoard(svgX, SCALE));
  const by = snapToGrid(svgToBoard(svgY, SCALE));
  // Clamp to board bounds
  return {
    x: Math.max(0, Math.min(60, bx)),
    y: Math.max(0, Math.min(44, by)),
  };
}

// Mouse tracking for crosshair / previews
const mouseBoard = ref<{ x: number; y: number } | null>(null);

function onSvgMouseMove(e: MouseEvent) {
  mouseBoard.value = svgPoint(e.clientX, e.clientY);
  if (isDragging.value) {
    continueDrag(e);
  }
}

// ─── Zone creation (press-drag-release) ───────────────────────────────────────
const creatingZone = ref(false);
const zoneAnchor = ref<{ x: number; y: number } | null>(null);
// Set when a drag created a zone, so the trailing click doesn't deselect it.
let suppressNextClick = false;
const zonePreview = computed(() => {
  if (!creatingZone.value || !zoneAnchor.value || !mouseBoard.value) return null;
  return {
    x1: zoneAnchor.value.x,
    y1: zoneAnchor.value.y,
    x2: mouseBoard.value.x,
    y2: mouseBoard.value.y,
  };
});

// ─── Arrow placement (two-click) ─────────────────────────────────────────────
const arrowFirstClick = ref(false);
const arrowStart = ref<{ x: number; y: number } | null>(null);

// ─── Drag state ───────────────────────────────────────────────────────────────
const isDragging = ref(false);
// Reactive: index of the zone whose label is currently being dragged (for the
// drag highlight in the template). Plain drag* state below isn't reactive.
const draggingLabelIdx = ref<number | null>(null);
let dragKind: ToolKind | null = null;
let dragIdx = -1;
let dragArrowEndpoint: 'start' | 'end' | null = null;
// Zone interaction: 'move' translates the whole rect (preserving grab offset);
// 'resize' moves one corner/edge while the opposite side stays fixed.
type ZoneHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
let dragZoneMode: 'move' | 'resize' | 'label' | null = null;
let dragZoneHandle: ZoneHandle | null = null;
let dragZoneOffset = { dx: 0, dy: 0 };
// Grab offset for point elements (objective/terrain/label) so dragging keeps the
// cursor's position within the element instead of snapping its anchor to the cursor.
let dragPointOffset = { dx: 0, dy: 0 };

// Half-size (px) of a zone resize handle square.
const HANDLE = 4;

function zoneHandles(zone: IMapZone): { key: ZoneHandle; x: number; y: number; cursor: string }[] {
  const left = Math.min(zone.x1, zone.x2);
  const right = Math.max(zone.x1, zone.x2);
  const top = Math.min(zone.y1, zone.y2);
  const bottom = Math.max(zone.y1, zone.y2);
  const midX = (left + right) / 2;
  const midY = (top + bottom) / 2;
  return [
    { key: 'nw', x: left, y: top, cursor: 'nwse-resize' },
    { key: 'n', x: midX, y: top, cursor: 'ns-resize' },
    { key: 'ne', x: right, y: top, cursor: 'nesw-resize' },
    { key: 'e', x: right, y: midY, cursor: 'ew-resize' },
    { key: 'se', x: right, y: bottom, cursor: 'nwse-resize' },
    { key: 's', x: midX, y: bottom, cursor: 'ns-resize' },
    { key: 'sw', x: left, y: bottom, cursor: 'nesw-resize' },
    { key: 'w', x: left, y: midY, cursor: 'ew-resize' },
  ];
}

function startDragPoint(kind: ToolKind, idx: number, e: MouseEvent) {
  selectElement(kind, idx);
  isDragging.value = true;
  dragKind = kind;
  dragIdx = idx;
  dragArrowEndpoint = null;
  dragZoneMode = null;
  dragZoneHandle = null;
  // Record where the cursor grabbed the element relative to its anchor.
  const pt = svgPoint(e.clientX, e.clientY);
  const el =
    kind === 'Objective'
      ? editMap.value.objectives[idx]
      : kind === 'Terrain'
        ? editMap.value.terrain[idx]
        : kind === 'Label'
          ? (editMap.value.labels ?? [])[idx]
          : kind === 'Icon'
            ? (editMap.value.icons ?? [])[idx]
            : null;
  dragPointOffset = pt && el ? { dx: pt.x - el.x, dy: pt.y - el.y } : { dx: 0, dy: 0 };
  window.addEventListener('mousemove', onWindowMouseMove);
  window.addEventListener('mouseup', endDrag);
  e.preventDefault();
}

function startDragZone(idx: number, e: MouseEvent) {
  selectElement('Zone', idx);
  isDragging.value = true;
  dragKind = 'Zone';
  dragIdx = idx;
  dragArrowEndpoint = null;
  dragZoneMode = 'move';
  dragZoneHandle = null;
  // Record where the cursor grabbed the zone relative to its top-left, so the
  // zone moves with the cursor instead of snapping a corner to it.
  const zone = editMap.value.zones[idx];
  const pt = svgPoint(e.clientX, e.clientY);
  if (zone && pt) {
    dragZoneOffset = {
      dx: pt.x - Math.min(zone.x1, zone.x2),
      dy: pt.y - Math.min(zone.y1, zone.y2),
    };
  } else {
    dragZoneOffset = { dx: 0, dy: 0 };
  }
  window.addEventListener('mousemove', onWindowMouseMove);
  window.addEventListener('mouseup', endDrag);
  e.preventDefault();
}

function startResizeZone(idx: number, handle: ZoneHandle, e: MouseEvent) {
  selectElement('Zone', idx);
  isDragging.value = true;
  dragKind = 'Zone';
  dragIdx = idx;
  dragArrowEndpoint = null;
  dragZoneMode = 'resize';
  dragZoneHandle = handle;
  window.addEventListener('mousemove', onWindowMouseMove);
  window.addEventListener('mouseup', endDrag);
  e.preventDefault();
}

// Zone label: when its zone is selected, grabbing the label drags only the label
// (setting labelDx/labelDy). When not selected, let the event bubble to the zone
// group so it selects/moves the whole zone as before.
function onZoneLabelMouseDown(idx: number, e: MouseEvent) {
  if (selectedKind.value === 'Zone' && selectedIdx.value === idx) {
    e.stopPropagation();
    startDragZoneLabel(idx, e);
  }
}

function startDragZoneLabel(idx: number, e: MouseEvent) {
  selectElement('Zone', idx);
  isDragging.value = true;
  dragKind = 'Zone';
  dragIdx = idx;
  draggingLabelIdx.value = idx;
  dragArrowEndpoint = null;
  dragZoneMode = 'label';
  dragZoneHandle = null;
  // Grab offset = cursor − current label position, so the label tracks the cursor
  // without snapping to it.
  const zone = editMap.value.zones[idx];
  const pt = svgPoint(e.clientX, e.clientY);
  if (zone && pt) {
    const labelX = (zone.x1 + zone.x2) / 2 + (zone.labelDx ?? 0);
    const labelY = (zone.y1 + zone.y2) / 2 + (zone.labelDy ?? 0);
    dragPointOffset = { dx: pt.x - labelX, dy: pt.y - labelY };
  } else {
    dragPointOffset = { dx: 0, dy: 0 };
  }
  window.addEventListener('mousemove', onWindowMouseMove);
  window.addEventListener('mouseup', endDrag);
  e.preventDefault();
}

function startDragArrowEndpoint(idx: number, endpoint: 'start' | 'end', e: MouseEvent) {
  selectElement('Arrow', idx);
  isDragging.value = true;
  dragKind = 'Arrow';
  dragIdx = idx;
  dragArrowEndpoint = endpoint;
  dragZoneMode = null;
  dragZoneHandle = null;
  window.addEventListener('mousemove', onWindowMouseMove);
  window.addEventListener('mouseup', endDrag);
  e.preventDefault();
}

function onWindowMouseMove(e: MouseEvent) {
  mouseBoard.value = svgPoint(e.clientX, e.clientY);
  continueDrag(e);
}

function continueDrag(e: MouseEvent) {
  if (!isDragging.value || !svgEl.value) return;
  const pt = svgPoint(e.clientX, e.clientY);
  if (!pt) return;

  // Point elements move by the grab offset (cursor − anchor), clamped to the board.
  const px = Math.max(0, Math.min(60, pt.x - dragPointOffset.dx));
  const py = Math.max(0, Math.min(44, pt.y - dragPointOffset.dy));

  if (dragKind === 'Objective' && dragIdx >= 0) {
    const obj = editMap.value.objectives[dragIdx];
    if (obj) {
      const updated = { ...obj, x: px, y: py };
      editMap.value = {
        ...editMap.value,
        objectives: editMap.value.objectives.map((o, i) => (i === dragIdx ? updated : o)),
      };
    }
  } else if (dragKind === 'Terrain' && dragIdx >= 0) {
    const t = editMap.value.terrain[dragIdx];
    if (t) {
      const updated = { ...t, x: px, y: py };
      editMap.value = {
        ...editMap.value,
        terrain: editMap.value.terrain.map((o, i) => (i === dragIdx ? updated : o)),
      };
    }
  } else if (dragKind === 'Label' && dragIdx >= 0) {
    const lbl = (editMap.value.labels ?? [])[dragIdx];
    if (lbl) {
      const updated = { ...lbl, x: px, y: py };
      const labels = (editMap.value.labels ?? []).map((l, i) => (i === dragIdx ? updated : l));
      editMap.value = { ...editMap.value, labels };
    }
  } else if (dragKind === 'Icon' && dragIdx >= 0) {
    const ic = (editMap.value.icons ?? [])[dragIdx];
    if (ic) {
      const updated = { ...ic, x: px, y: py };
      const icons = (editMap.value.icons ?? []).map((o, i) => (i === dragIdx ? updated : o));
      editMap.value = { ...editMap.value, icons };
    }
  } else if (dragKind === 'Zone' && dragIdx >= 0 && dragZoneMode === 'move') {
    const zone = editMap.value.zones[dragIdx];
    if (zone) {
      const left = Math.min(zone.x1, zone.x2);
      const top = Math.min(zone.y1, zone.y2);
      const w = Math.abs(zone.x2 - zone.x1);
      const h = Math.abs(zone.y2 - zone.y1);
      // New top-left from the cursor, preserving the grab offset; clamp so the
      // whole zone stays on the 60x44 board.
      const newLeft = Math.max(0, Math.min(pt.x - dragZoneOffset.dx, 60 - w));
      const newTop = Math.max(0, Math.min(pt.y - dragZoneOffset.dy, 44 - h));
      const updated: IMapZone = {
        ...zone,
        x1: newLeft,
        y1: newTop,
        x2: newLeft + w,
        y2: newTop + h,
      };
      editMap.value = {
        ...editMap.value,
        zones: editMap.value.zones.map((z, i) => (i === dragIdx ? updated : z)),
      };
    }
  } else if (dragKind === 'Zone' && dragIdx >= 0 && dragZoneMode === 'label') {
    const zone = editMap.value.zones[dragIdx];
    if (zone) {
      const cx = (zone.x1 + zone.x2) / 2;
      const cy = (zone.y1 + zone.y2) / 2;
      // px/py are the cursor minus grab offset, snapped + clamped to the board.
      const updated: IMapZone = { ...zone, labelDx: px - cx, labelDy: py - cy };
      editMap.value = {
        ...editMap.value,
        zones: editMap.value.zones.map((z, i) => (i === dragIdx ? updated : z)),
      };
    }
  } else if (dragKind === 'Zone' && dragIdx >= 0 && dragZoneMode === 'resize' && dragZoneHandle) {
    const zone = editMap.value.zones[dragIdx];
    if (zone) {
      let left = Math.min(zone.x1, zone.x2);
      let right = Math.max(zone.x1, zone.x2);
      let top = Math.min(zone.y1, zone.y2);
      let bottom = Math.max(zone.y1, zone.y2);
      // Move only the dragged edge(s); the opposite side stays fixed. pt is
      // already snapped + clamped to the board.
      if (dragZoneHandle.includes('w')) left = pt.x;
      if (dragZoneHandle.includes('e')) right = pt.x;
      if (dragZoneHandle.includes('n')) top = pt.y;
      if (dragZoneHandle.includes('s')) bottom = pt.y;
      // Normalize in case an edge was dragged past its opposite.
      const updated: IMapZone = {
        ...zone,
        x1: Math.min(left, right),
        x2: Math.max(left, right),
        y1: Math.min(top, bottom),
        y2: Math.max(top, bottom),
      };
      editMap.value = {
        ...editMap.value,
        zones: editMap.value.zones.map((z, i) => (i === dragIdx ? updated : z)),
      };
    }
  } else if (dragKind === 'Arrow' && dragIdx >= 0 && dragArrowEndpoint) {
    const arrow = (editMap.value.arrows ?? [])[dragIdx];
    if (arrow) {
      const updated: IMapArrow =
        dragArrowEndpoint === 'start'
          ? { ...arrow, x1: pt.x, y1: pt.y }
          : { ...arrow, x2: pt.x, y2: pt.y };
      const arrows = (editMap.value.arrows ?? []).map((a, i) => (i === dragIdx ? updated : a));
      editMap.value = { ...editMap.value, arrows };
    }
  }
}

function endDrag() {
  if (isDragging.value) {
    isDragging.value = false;
    commit();
    // Swallow the click that fires after the drag's mouseup so it doesn't
    // deselect the element — e.g. when the release lands on the board background
    // or outside the canvas entirely.
    suppressNextClick = true;
  }
  dragKind = null;
  dragIdx = -1;
  draggingLabelIdx.value = null;
  dragArrowEndpoint = null;
  dragZoneMode = null;
  dragZoneHandle = null;
  window.removeEventListener('mousemove', onWindowMouseMove);
  window.removeEventListener('mouseup', endDrag);
}

// ─── Click placement ──────────────────────────────────────────────────────────
function onSvgClick(_e: MouseEvent) {
  // handled by onSvgClickEvent (combined handler on the svg element)
}

function onSvgClickEvent(e: MouseEvent) {
  // Ignore if a drag just finished
  if (isDragging.value) return;
  // Ignore the click that fires right after a drag-created zone, so it doesn't
  // immediately deselect the new zone.
  if (suppressNextClick) {
    suppressNextClick = false;
    return;
  }
  const pt = svgPoint(e.clientX, e.clientY);
  if (!pt) return;

  // If an element group handled the click it already called selectElement() and
  // stopped propagation — we only reach here for background clicks.
  if (e.target !== svgEl.value && (e.target as SVGElement).closest('.element-group')) {
    return;
  }

  const isBackgroundClick =
    e.target === svgEl.value || (e.target as SVGElement).classList.contains('map-bg');

  // Priority 1 — multi-click arrow placement in progress → complete it.
  // (Zones are created by press-drag-release in onSvgMouseDown, not by clicks.)
  if (activeTool.value === 'Arrow' && arrowFirstClick.value) {
    handleArrowClick(pt);
    return;
  }

  // Priority 2 — Select tool only: clicking the background clears the selection.
  // Placement tools fall through to Priority 3 and place a new element (which
  // then auto-selects), so a background click keeps placing rather than deselecting.
  if (isBackgroundClick && activeTool.value === 'Select' && selectedKind.value !== null) {
    deselectElement();
    return;
  }

  // Priority 3 — normal placement.
  switch (activeTool.value) {
    case 'Objective':
      placeObjective(pt);
      break;
    case 'Terrain':
      placeTerrain(pt);
      break;
    case 'Label':
      placeLabel(pt);
      break;
    case 'Arrow':
      handleArrowClick(pt);
      break;
    case 'Icon':
      placeIcon(pt);
      break;
  }
}

function onSvgMouseDown(e: MouseEvent) {
  if (e.button !== 0 || activeTool.value !== 'Zone') return;
  // Only start on the empty board — element/handle mousedowns use @mousedown.stop.
  const target = e.target as SVGElement;
  if (target !== svgEl.value && !target.classList.contains('map-bg')) return;
  const pt = svgPoint(e.clientX, e.clientY);
  if (!pt) return;
  creatingZone.value = true;
  zoneAnchor.value = pt;
  mouseBoard.value = pt;
  window.addEventListener('mousemove', onZoneCreateMove);
  window.addEventListener('mouseup', finishZoneCreate);
  e.preventDefault();
}

function onZoneCreateMove(e: MouseEvent) {
  mouseBoard.value = svgPoint(e.clientX, e.clientY);
}

function finishZoneCreate(e: MouseEvent) {
  window.removeEventListener('mousemove', onZoneCreateMove);
  window.removeEventListener('mouseup', finishZoneCreate);
  const anchor = zoneAnchor.value;
  const pt = svgPoint(e.clientX, e.clientY);
  creatingZone.value = false;
  zoneAnchor.value = null;
  if (!anchor || !pt) return;
  const x1 = Math.min(anchor.x, pt.x);
  const y1 = Math.min(anchor.y, pt.y);
  const x2 = Math.max(anchor.x, pt.x);
  const y2 = Math.max(anchor.y, pt.y);
  if (x2 - x1 === 0 || y2 - y1 === 0) return; // pure click, no drag → no zone
  const zone: IMapZone = { type: zoneType.value, x1, y1, x2, y2 };
  editMap.value = { ...editMap.value, zones: [...editMap.value.zones, zone] };
  commit();
  maybeSelectPlaced('Zone', editMap.value.zones.length - 1, true);
}

function placeObjective(pt: { x: number; y: number }) {
  const obj: IMapObjective = {
    type: objType.value,
    x: pt.x,
    y: pt.y,
  };
  const m = placementMirror('Objective');
  if (m !== 'none') obj.mirror = m as Mirror;
  editMap.value = {
    ...editMap.value,
    objectives: [...editMap.value.objectives, obj],
  };
  commit();
  maybeSelectPlaced('Objective', editMap.value.objectives.length - 1);
}

function placeTerrain(pt: { x: number; y: number }) {
  const t: IMapTerrain = {
    type: terrainType.value,
    size: terrainSize.value,
    x: pt.x,
    y: pt.y,
  };
  const m = placementMirror('Terrain');
  if (m !== 'none') t.mirror = m as Mirror;
  editMap.value = {
    ...editMap.value,
    terrain: [...editMap.value.terrain, t],
  };
  commit();
  maybeSelectPlaced('Terrain', editMap.value.terrain.length - 1);
}

function placeIcon(pt: { x: number; y: number }) {
  const icon: IMapIcon = { icon: iconName.value, x: pt.x, y: pt.y };
  editMap.value = {
    ...editMap.value,
    icons: [...(editMap.value.icons ?? []), icon],
  };
  commit();
  maybeSelectPlaced('Icon', (editMap.value.icons?.length ?? 1) - 1);
}

// Label/arrow text is collected through a styled modal (NameModal) instead of a
// native window.prompt. The pending placement geometry is stashed while the modal
// is open and consumed on confirm.
const labelModalOpen = ref(false);
const labelModalKind = ref<'label' | 'arrow'>('label');
const pendingLabelPt = ref<{ x: number; y: number } | null>(null);
const pendingArrow = ref<{ start: { x: number; y: number }; end: { x: number; y: number } } | null>(
  null,
);

const labelModalConfig = computed(() =>
  labelModalKind.value === 'label'
    ? {
        title: 'Add label',
        fieldLabel: 'Label text',
        placeholder: 'Label text…',
        confirmLabel: 'Add label',
        allowEmpty: false,
      }
    : {
        title: 'Add arrow',
        fieldLabel: 'Arrow label',
        placeholder: 'Optional label…',
        confirmLabel: 'Add arrow',
        allowEmpty: true,
      },
);

function placeLabel(pt: { x: number; y: number }) {
  pendingLabelPt.value = pt;
  labelModalKind.value = 'label';
  labelModalOpen.value = true;
}

function handleArrowClick(pt: { x: number; y: number }) {
  if (!arrowFirstClick.value) {
    arrowFirstClick.value = true;
    arrowStart.value = pt;
    return;
  }
  // Second click defines the arrow; stash it and collect the optional label.
  pendingArrow.value = { start: arrowStart.value!, end: pt };
  arrowFirstClick.value = false;
  arrowStart.value = null;
  labelModalKind.value = 'arrow';
  labelModalOpen.value = true;
}

function confirmLabelModal(text: string) {
  const trimmed = text.trim();
  if (labelModalKind.value === 'label') {
    const pt = pendingLabelPt.value;
    if (pt && trimmed) {
      const label: IMapLabel = { text: trimmed, x: pt.x, y: pt.y };
      editMap.value = { ...editMap.value, labels: [...(editMap.value.labels ?? []), label] };
      commit();
      maybeSelectPlaced('Label', (editMap.value.labels?.length ?? 1) - 1);
    }
  } else {
    const a = pendingArrow.value;
    if (a) {
      const arrow: IMapArrow = { x1: a.start.x, y1: a.start.y, x2: a.end.x, y2: a.end.y };
      if (trimmed) arrow.text = trimmed;
      editMap.value = { ...editMap.value, arrows: [...(editMap.value.arrows ?? []), arrow] };
      commit();
      maybeSelectPlaced('Arrow', (editMap.value.arrows?.length ?? 1) - 1);
    }
  }
  closeLabelModal();
}

function closeLabelModal() {
  labelModalOpen.value = false;
  pendingLabelPt.value = null;
  pendingArrow.value = null;
}

// ─── Undo ─────────────────────────────────────────────────────────────────────
function doUndo() {
  ws.undo();
  syncEditMapFromStore();
  deselectElement();
  resetPlacementState();
}

// ─── Delete selected element ──────────────────────────────────────────────────
function onKeyDown(e: KeyboardEvent) {
  // Don't hijack typing in form fields (inspector inputs, forms, etc.)
  const t = e.target as HTMLElement | null;
  const tag = t?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;

  if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
    e.preventDefault();
    doUndo();
  } else if (e.key === 'Delete' || e.key === 'Backspace') {
    deleteSelected();
  } else if (e.key === 'Escape') {
    resetPlacementState();
    deselectElement();
  }
}

// Clicking anywhere in the editor area (around the canvas) deselects — but not
// when clicking the SVG itself (it manages its own clicks), the toolbar, or the
// inspector (those are interactive and must not clear the selection).
function onEditorAreaClick(e: MouseEvent) {
  // A drag that ended outside the svg fires its click here (bubbling to the
  // editor root); swallow it so it doesn't deselect what we just moved.
  if (suppressNextClick) {
    suppressNextClick = false;
    return;
  }
  const el = e.target as HTMLElement;
  // Clicks anywhere in the bar system (title/tool/sub-bar/props) or the modal
  // must not clear the selection — those controls edit the current selection.
  if (el.closest('svg') || el.closest('.toolbar-stack') || el.closest('.modal-overlay')) {
    return;
  }
  if (selectedKind.value !== null) deselectElement();
}

onMounted(() => window.addEventListener('keydown', onKeyDown));
onUnmounted(() => window.removeEventListener('keydown', onKeyDown));

function deleteSelected() {
  if (selectedKind.value === null || selectedIdx.value === null) return;
  const idx = selectedIdx.value;
  switch (selectedKind.value) {
    case 'Objective':
      editMap.value = {
        ...editMap.value,
        objectives: editMap.value.objectives.filter((_, i) => i !== idx),
      };
      break;
    case 'Terrain':
      editMap.value = {
        ...editMap.value,
        terrain: editMap.value.terrain.filter((_, i) => i !== idx),
      };
      break;
    case 'Zone':
      editMap.value = { ...editMap.value, zones: editMap.value.zones.filter((_, i) => i !== idx) };
      break;
    case 'Label':
      editMap.value = {
        ...editMap.value,
        labels: (editMap.value.labels ?? []).filter((_, i) => i !== idx),
      };
      break;
    case 'Arrow':
      editMap.value = {
        ...editMap.value,
        arrows: (editMap.value.arrows ?? []).filter((_, i) => i !== idx),
      };
      break;
    case 'Icon':
      editMap.value = {
        ...editMap.value,
        icons: (editMap.value.icons ?? []).filter((_, i) => i !== idx),
      };
      break;
  }
  commit();
  deselectElement();
}

// ─── Reset placement state ────────────────────────────────────────────────────
function resetPlacementState() {
  creatingZone.value = false;
  zoneAnchor.value = null;
  suppressNextClick = false;
  arrowFirstClick.value = false;
  arrowStart.value = null;
  window.removeEventListener('mousemove', onZoneCreateMove);
  window.removeEventListener('mouseup', finishZoneCreate);
}
</script>

<style scoped>
.map-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.no-plan-hint {
  color: var(--st-text-subtle);
  font-style: italic;
  margin-top: var(--st-space-6);
  text-align: center;
}

/* ── Toolbar ─────────────────────────────────────────────────────────────── */
/* The tool row + sub-bar + inspector flow directly under the shared header (the
   surface wraps header + panes in one rounded card), so this is a plain group —
   its bottom border separates the tools from the board below. */
.toolbar-stack {
  background: var(--st-surface);
  border-bottom: 1px solid var(--st-border);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--st-space-2);
  padding: var(--st-space-2) var(--st-space-3);
  background: var(--st-surface);
  border-bottom: 1px solid var(--st-border);
}

.toolbar .st-toolbar-spacer {
  flex: 1;
}

.actions-caret {
  font-size: 0.7em;
  opacity: 0.7;
}

/* ── Board container ─────────────────────────────────────────────────────── */
.board-container {
  flex: 1;
  overflow: auto;
  padding: var(--st-space-3);
}

.editor-svg {
  width: 100%;
  max-width: 720px;
  height: auto;
  display: block;
  cursor: crosshair;
  outline: none;
  border-radius: 0.25rem;
}

/* ── SVG elements ────────────────────────────────────────────────────────── */
/* Zones */
.zone-attacker {
  fill: rgba(200, 60, 60, 0.15);
  stroke: rgba(200, 60, 60, 0.6);
  stroke-width: 1.5;
  stroke-dasharray: 8 4;
}
.zone-defender {
  fill: rgba(60, 120, 200, 0.15);
  stroke: rgba(60, 120, 200, 0.6);
  stroke-width: 1.5;
  stroke-dasharray: 8 4;
}
.zone-other {
  fill: rgba(180, 180, 60, 0.12);
  stroke: rgba(180, 180, 60, 0.5);
  stroke-width: 1.5;
  stroke-dasharray: 8 4;
}
.zone-label {
  fill: #374151;
  font-size: 16px;
  font-weight: 600;
  text-anchor: middle;
  dominant-baseline: central;
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 2px;
  pointer-events: none;
}

.zone-label-draggable {
  pointer-events: all;
  cursor: move;
}
/* When the label is grabbable (its zone is selected) or actively dragging,
   bold + darken it so users can tell it's movable. */
.zone-label-draggable:hover,
.zone-label-dragging {
  font-weight: 800;
  opacity: 0.95;
  fill: #1f2937;
}

/* Objectives */
.objective {
  stroke-width: 2;
}
.objective-regular {
  fill: var(--color-yellow);
  stroke: var(--color-yellow);
  opacity: 0.7;
}
.objective-primary {
  fill: rgba(255, 215, 0, 0.9);
  stroke: #b8960f;
}
.objective-secondary {
  fill: rgba(192, 192, 192, 0.9);
  stroke: #888;
}
.objective-oakenbrow {
  fill: #dc2626;
  stroke: #dc2626;
  opacity: 0.7;
}
.objective-gnarlroot {
  fill: #16a34a;
  stroke: #16a34a;
  opacity: 0.7;
}
.objective-heartwood {
  fill: #2563eb;
  stroke: #2563eb;
  opacity: 0.7;
}
.objective-winterleaf {
  fill: #7c3aed;
  stroke: #7c3aed;
  opacity: 0.7;
}
.objective-golden_lions {
  fill: #dc2626;
  stroke: #dc2626;
  opacity: 0.7;
}
.objective-sun_seekers {
  fill: #d7b21d;
  stroke: #d7b21d;
  opacity: 0.7;
}
.objective-heldenhain {
  fill: #171717;
  stroke: #171717;
  opacity: 0.7;
}
.objective-sigmarite {
  fill: #2563eb;
  stroke: #2563eb;
  opacity: 0.7;
}

.objective-label {
  fill: #fff;
  font-size: 13px;
  font-weight: 700;
  text-anchor: middle;
  dominant-baseline: central;
  pointer-events: none;
}
/* Objective glyph (e.g. the regular-objective star) drawn in place of a letter. */
.objective-icon-svg {
  fill: #fff;
}

/* Terrain */
.terrain {
  stroke-width: 1.5;
}
.terrain-ruin,
.terrain-obscuring,
.terrain-place_of_power {
  fill: #9ca3af;
  stroke: #6b7280;
  opacity: 0.6;
}

/* Arrows */
.map-arrow {
  stroke: #374151;
  stroke-width: 2;
  opacity: 0.8;
}
.arrow-label {
  fill: #374151;
  font-size: 11px;
  font-weight: 600;
  text-anchor: middle;
  pointer-events: none;
}

/* Labels */
.map-label {
  fill: #374151;
  font-size: 12px;
  font-weight: 600;
  text-anchor: middle;
  dominant-baseline: central;
  opacity: 0.85;
}

/* Ghost elements — mirrored, non-interactive derived copies */
.ghost-element {
  opacity: 0.4;
  stroke-dasharray: 6 3;
}
.ghost-label {
  opacity: 0.4;
}

/* Terrain icons */
.terrain-icon-svg {
  fill: var(--text-main, #374151);
  opacity: 0.8;
}

.map-icon-svg {
  fill: var(--text-main, #374151);
  opacity: 0.9;
}

/* Invisible but fully hittable footprint for an icon (fill:none would still let
   clicks fall through; pointer-events:all captures the whole disc). */
.icon-hit {
  fill: transparent;
  pointer-events: all;
}

/* Selection — editor-interaction overlays (use the studio accent, not board colors) */
.element-group.selected > rect,
.element-group.selected > circle:first-child,
.element-group.selected > line {
  filter: drop-shadow(0 0 4px var(--st-accent));
}

.selection-handle {
  fill: var(--st-accent);
  stroke: var(--st-white);
  stroke-width: 1.5;
  pointer-events: all;
  cursor: grab;
}

.anchor-dot {
  fill: var(--st-accent);
  stroke: var(--st-white);
  stroke-width: 1.5;
}

.zone-resize-handle {
  fill: var(--st-white);
  stroke: var(--st-accent-hover);
  stroke-width: 1.5;
  pointer-events: all;
}

/* Crosshair */
.crosshair-line {
  stroke: var(--st-accent);
  stroke-width: 0.5;
  stroke-dasharray: 4 4;
  opacity: 0.4;
}
.coord-label {
  fill: var(--st-text);
  font-size: 10px;
  font-weight: 600;
  opacity: 0.7;
}
</style>
